import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Select, Spin, Tag, message } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import apiService from '../../services/api';
import { useChannels } from '../../hooks/useChannels';
import { COLOR_PRIMARY } from '../../utils/colors';

const { Title, Text } = Typography;
const { Option } = Select;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const fmt = (n, decimals = 2) =>
  n == null ? '—' : `£${Number(n).toFixed(decimals)}`;

const columns = [
  {
    title: '#',
    key: 'rank',
    width: 44,
    render: (_, __, i) => <Text type="secondary" style={{ fontWeight: 600 }}>{i + 1}</Text>,
  },
  {
    title: 'Collection',
    dataIndex: 'from',
    key: 'from',
    render: val => <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 13 }}>{val}</Tag>,
  },
  {
    title: '',
    key: 'arrow',
    width: 32,
    render: () => <ArrowRightOutlined style={{ color: '#999' }} />,
  },
  {
    title: 'Delivery',
    dataIndex: 'to',
    key: 'to',
    render: val => <Tag color="orange" style={{ fontFamily: 'monospace', fontSize: 13 }}>{val}</Tag>,
  },
  {
    title: 'Jobs',
    dataIndex: 'jobCount',
    key: 'jobCount',
    width: 64,
    sorter: (a, b) => a.jobCount - b.jobCount,
    render: val => <Text strong>{val}</Text>,
  },
  {
    title: 'Avg Distance',
    dataIndex: 'avgDistanceMiles',
    key: 'avgDistanceMiles',
    width: 150,
    sorter: (a, b) => a.avgDistanceMiles - b.avgDistanceMiles,
    render: (val, row) => val != null
      ? (
        <span>
          {val} mi{' '}
          {row.distanceSource === 'straight-line' && (
            <Text type="secondary" style={{ fontSize: 11 }}>~</Text>
          )}
        </span>
      )
      : '—',
  },
  {
    title: 'Avg Cost',
    dataIndex: 'avgCostGbp',
    key: 'avgCostGbp',
    width: 110,
    sorter: (a, b) => a.avgCostGbp - b.avgCostGbp,
    render: val => fmt(val),
  },
  {
    title: 'Cost / Mile',
    dataIndex: 'avgCostPerMile',
    key: 'avgCostPerMile',
    width: 120,
    defaultSortOrder: 'descend',
    sorter: (a, b) => a.avgCostPerMile - b.avgCostPerMile,
    render: val => (
      <Text strong style={{ color: COLOR_PRIMARY }}>{fmt(val)}</Text>
    ),
  },
];

const MileageCost = () => {
  const [loading, setLoading] = useState(true);
  const [corridors, setCorridors] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [channel, setChannel] = useState('');
  const { channels } = useChannels({ activeOnly: true });

  useEffect(() => {
    setLoading(true);
    const params = { year };
    if (channel) params.channel = channel;
    apiService.reports.getMileageCost(params)
      .then(res => setCorridors(res.data.corridors || []))
      .catch(() => message.error('Failed to load mileage cost data'))
      .finally(() => setLoading(false));
  }, [year, channel]);

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 4 }}>Mileage Cost by Postcode</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Average job cost and cost per mile for each collection → delivery corridor
      </Text>

      <Card>
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <Text strong>Filters:</Text>
          <Select value={year} onChange={setYear} style={{ width: 90 }}>
            {YEAR_OPTIONS.map(y => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          <Select value={channel} onChange={setChannel} style={{ width: 180 }} placeholder="All Channels">
            <Option value="">All Channels</Option>
            {channels.map(c => <Option key={c._id} value={c.name}>{c.name}</Option>)}
          </Select>
          {!loading && (
            <Text type="secondary" style={{ marginLeft: 'auto' }}>
              Top {corridors.length} corridors
            </Text>
          )}
        </div>

        <Spin spinning={loading}>
          <Table
            dataSource={corridors}
            columns={columns}
            rowKey={(row, i) => `${row.from}-${row.to}-${i}`}
            pagination={false}
            size="middle"
            scroll={{ x: 700 }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default MileageCost;
